<template>
  <div class="root">
    <LoadingSpinner v-if="isLoading" size="medium" variant="inline" />

    <p v-else-if="loadFailed" class="load-error" role="alert">{{ $t("eventContest.error.fetchFailed") }}</p>

    <article v-else-if="contest" class="panel">
      <img v-if="imageUrl" class="hero" :src="imageUrl" :alt="contestTitle" />
      <div class="body">
        <h1 class="title">{{ $t("eventContestTerms.title") }}</h1>
        <div v-if="termsHtml" class="content" v-html="termsHtml" />
        <p class="consent-note">{{ $t("eventContestTerms.minorConsent") }}</p>
        <button type="button" class="agree-button" :disabled="!canApply" @click="onClickAgree">
          {{ $t("eventContestTerms.agreeButton") }}
        </button>
      </div>
    </article>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { NolaPfContestClient } from "@/lib/clients";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";
import { pushWithTermsAgreement } from "@/lib/utils/contestEntryFormHistory";
import { optimizeImage } from "@/lib/utils/url";

const nolaPfContestClient = new NolaPfContestClient();

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

export default Vue.extend<Data, Methods, Computed, Props>({
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    LoadingSpinner,
  },
  props: {
    id: String,
  },
  data() {
    return {
      isLoading: true,
      loadFailed: false,
      contest: null,
    };
  },
  async created() {
    if (!this.id) {
      this.loadFailed = true;
      this.isLoading = false;
      return;
    }

    try {
      this.contest = await nolaPfContestClient.fetchMicroCmsContest(this.id);
    } catch {
      this.loadFailed = true;
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    contestTitle() {
      return this.contest?.contest ?? "";
    },
    imageUrl() {
      const url = this.contest?.image?.url;
      if (!url) return "";
      return optimizeImage(url);
    },
    termsHtml() {
      const terms = this.contest?.terms ?? "";
      if (!terms) return "";
      const source = HTML_TAG_PATTERN.test(terms) ? terms : terms.replace(/\n/g, "<br>");
      return this.$sanitize(source);
    },
    canApply() {
      return this.contest?.contestStatus === "accepting";
    },
  },
  methods: {
    async onClickAgree() {
      if (!this.canApply || !this.id) return;

      await pushWithTermsAgreement(
        (location) => this.$router.push(location),
        {
          name: "eventContestEntryFormStep1",
          params: { id: this.id },
        },
        this.id
      );
    },
  },
});

interface Props {
  id: string;
}

interface Data {
  isLoading: boolean;
  loadFailed: boolean;
  contest: NolaPfMicroCmsContest | null;
}

interface Computed {
  contestTitle: string;
  imageUrl: string;
  termsHtml: string;
  canApply: boolean;
}

interface Methods {
  onClickAgree(): Promise<void>;
}
</script>

<style lang="scss" scoped>
.root {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: calc(100vh - #{$header-height});
  overflow: hidden;
  background-color: var(--color-background-normal);
  padding: 40px 16px;

  @include mq("sp") {
    height: 100%;
    min-height: 0;
    padding: 0;
  }
}

.load-error {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.panel {
  flex: 1 1 auto;
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 0;
  max-width: 740px;
  overflow-y: auto;
  background-color: var(--color-surface-base);

  @include mq("sp") {
    max-width: none;
  }
}

.hero {
  display: block;
  width: 100%;
  height: auto;
}

.body {
  box-sizing: border-box;
  padding: 40px;

  @include mq("sp") {
    padding: 24px 16px;
  }
}

.title {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  word-break: break-word;
}

.content {
  margin-top: 24px;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  overflow-wrap: anywhere;
  word-break: break-word;

  ::v-deep img,
  ::v-deep video,
  ::v-deep iframe,
  ::v-deep table {
    max-width: 100%;
  }

  ::v-deep img,
  ::v-deep video {
    height: auto;
  }

  ::v-deep pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
}

.consent-note {
  margin: 24px 0 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  text-align: center;
  overflow-wrap: anywhere;
  word-break: break-word;

  @include mq("sp") {
    text-align: left;
  }
}

.agree-button {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: fit-content;
  min-width: 188px;
  max-width: 100%;
  height: 40px;
  margin: 24px auto 0;
  padding: 8px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;
  }

  @include mq("sp") {
    width: 100%;
    min-width: 0;
  }
}
</style>
